// src/pages/Signatures.jsx
import React, { useEffect, useState } from 'react';
import SignatureForm from '../components/SignatureForm';
import '../pages/Signatures.css';
import { api, endpoints } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { Alert, Button, Spinner, Toggle } from '../components/ui';
import useOrganizationBrandColors from '../hooks/useOrganizationBrandColors';
import useOrganizationBrandAssets from '../hooks/useOrganizationBrandAssets';
import useOverlayClose from '../hooks/useOverlayClose';
import { LANGUAGE_ORDER } from '../constants/languages';
import { useT } from '../i18n';

const messages = {
  docTitle: { tr: 'İmzalar - Signature Manager', en: 'Signatures - Signature Manager' },
  saveFailed: { tr: 'Şablon kaydedilemedi: {msg}', en: 'Couldn’t save the template: {msg}' },
  confirmDelete: { tr: 'Bu şablonu silmek istediğinize emin misiniz?', en: 'Are you sure you want to delete this template?' },
  deleteFailed: { tr: 'Şablon silinemedi: {msg}', en: 'Couldn’t delete the template: {msg}' },
  toggleFailed: { tr: 'Şablon durumu değiştirilemedi: {msg}', en: 'Couldn’t change the template status: {msg}' },
  updateFailed: { tr: 'Şablon güncellenemedi: {msg}', en: 'Couldn’t update the template: {msg}' },
  copySuffix: { tr: '(Kopya)', en: '(Copy)' },
  duplicateFailed: { tr: 'Şablon çoğaltılamadı: {msg}', en: 'Couldn’t duplicate the template: {msg}' },
  title: { tr: 'İmza Şablonları', en: 'Signature Templates' },
  subtitle: {
    tr: '{n} şablon · gruplara öncelikle atanmış',
    en: ({ n }) => `${n} template${n === 1 ? '' : 's'} · assigned to groups by priority`,
  },
  search: { tr: 'Şablon veya grup adı ara', en: 'Search template or group name' },
  newTemplate: { tr: '+ Yeni Şablon', en: '+ New Template' },
  error: { tr: 'Hata: {msg}', en: 'Error: {msg}' },
  fieldName: { tr: 'Şablon adı', en: 'Template name' },
  fieldHtml: { tr: 'İmza içeriği', en: 'Signature content' },
  fieldVariants: { tr: 'Dil seçenekleri', en: 'Languages' },
  fieldAssignments: { tr: 'Grup atamaları', en: 'Group assignments' },
  fieldUsers: { tr: 'Kişiye özel atama', en: 'Personal assignment' },
  groupsLoading: { tr: 'Gruplar yükleniyor…', en: 'Loading groups…' },
  editTitle: { tr: 'Şablonu Düzenle', en: 'Edit Template' },
  createTitle: { tr: 'Yeni Şablon Oluştur', en: 'Create New Template' },
  langTitle: { tr: 'Dil: {label}', en: 'Language: {label}' },
  people: { tr: '{n} kişi', en: ({ n }) => `${n} ${n === 1 ? 'person' : 'people'}` },
  duplicate: { tr: 'Çoğalt', en: 'Duplicate' },
  makeActive: { tr: 'Şablonu aktif yap', en: 'Activate template' },
  makeInactive: { tr: 'Şablonu pasif yap', en: 'Deactivate template' },
  inactiveTitle: { tr: 'Pasif — dağıtılmıyor. Aktif yapmak için tıklayın', en: 'Inactive — not being distributed. Click to activate' },
  activeTitle: { tr: 'Aktif — atanan gruplara dağıtılıyor. Pasif yapmak için tıklayın', en: 'Active — distributed to assigned groups. Click to deactivate' },
  deleteAria: { tr: 'Şablonu sil', en: 'Delete template' },
  newHint: { tr: 'Sıfırdan veya hazır şablondan', en: 'From scratch or from a ready-made template' },
};

export default function Signatures() {
  const { user } = useAuth();
  const [brandColors] = useOrganizationBrandColors();
  const [brandAssets] = useOrganizationBrandAssets();
  const overlayClose = useOverlayClose(() => { setShowForm(false); setEditSig(null); });
  const { t } = useT(messages);
  const FIELD_LABELS = {
    name: t('fieldName'), html_content: t('fieldHtml'), language_variants: t('fieldVariants'),
    assignments: t('fieldAssignments'), user_assignments: t('fieldUsers'),
  };
  // DRF's {field: [messages]} as one readable line.
  const describeError = (err) => {
    const data = err.response?.data;
    if (!data || typeof data !== 'object') return err.message;
    if (typeof data.error === 'string') return data.error;
    return Object.entries(data).map(([k, v]) => {
      const text = Array.isArray(v) ? v.map(x => (typeof x === 'string' ? x : JSON.stringify(x))).join(' ') : (typeof v === 'string' ? v : JSON.stringify(v));
      return k === 'non_field_errors' || k === 'detail' ? text : `${FIELD_LABELS[k] || k}: ${text}`;
    }).join(' | ');
  };

  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  // Save errors of the open create/edit modal — shown inside it, next to
  // the Save button, not on the page behind the overlay.
  const [formError, setFormError] = useState(null);
  const [editSig, setEditSig] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [directoryUsers, setDirectoryUsers] = useState([]);
  const [search, setSearch] = useState('');

  // 1) İmzaları API'den çek
  const loadSignatures = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(endpoints.signatures.templates.list);
      setSignatures(data);
    } catch (err) {
      console.error('İmzalar yüklenirken hata:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSignatures();
  }, []);

  const filteredSignatures = signatures.filter(sig => {
    const q = search.toLowerCase();
    if (!q) return true;
    const matchesName = sig.name && sig.name.toLowerCase().includes(q);
    const matchesGroup = Array.isArray(sig.assignments) &&
      sig.assignments.some(a => a.group_name && a.group_name.toLowerCase().includes(q));
    return matchesName || matchesGroup;
  });

  // Modal açıldığında grupları çek
  const fetchGroups = async () => {
    setGroupsLoading(true);
    try {
      const [data, users] = await Promise.all([
        api.get(endpoints.signatures.groups.visibility),
        // Directory users for "Kişiye Özel Atama"; best-effort — the section
        // still works (for already-assigned people) if the directory is down.
        api.get(endpoints.users).catch(() => []),
      ]);
      // Sadece visible olanları göster
      setGroups(data.filter(g => g.visible).map(g => ({ id: g.group_name, name: g.group_name })));
      setDirectoryUsers(Array.isArray(users) ? users : []);
    } catch (err) {
      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  };

  // Modal açıldığında grupları çek
  useEffect(() => {
    if (showForm || editSig) fetchGroups();
  }, [showForm, editSig]);

  // 2) Yeni şablonu kaydet (payload’u backend alan adlarıyla eşle)
  const handleSave = async payload => {
    setFormError(null);
    const body = {
      name: payload.name,                      // modeldeki name
      html_content: payload.html,               // html → html_content
      language: payload.language,
      language_variants: payload.language_variants,
      organization: user.organization,         // organization_id (REQUIRED)
      assignments: payload.assignments.map(a => ({
        group_name: a.group,                   // frontend’deki group → backend’in group_name
        priority: a.priority
      })),
      user_assignments: payload.user_assignments,
      survey: payload.survey,
    };

    try {
      await api.post('/api/signatures/templates/create/', body);
      // Başarılıysa formu kapat ve listeyi yenile
      setShowForm(false);
      await loadSignatures();

    } catch (err) {
      console.error('Şablon kaydederken hata:', err);
      setFormError(t('saveFailed', { msg: describeError(err) }));
    }
  };

  // 3) Şablon silme fonksiyonu
  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    setError(null);
    try {
      await api.delete(endpoints.signatures.templates.detail(id));
      await loadSignatures();
    } catch (err) {
      setError(t('deleteFailed', { msg: err.message }));
    }
  };

  // Pasife alınan şablon hiçbir kullanıcıya dağıtılmaz ama listede kalır ve düzenlenebilir.
  const handleToggleActive = async (sig, next) => {
    setError(null);
    setSignatures(prev => prev.map(s => (s.id === sig.id ? { ...s, is_active: next } : s)));
    try {
      await api.patch(endpoints.signatures.templates.detail(sig.id), { is_active: next });
    } catch (err) {
      setError(t('toggleFailed', { msg: err.response?.data?.error || err.message }));
      await loadSignatures();
    }
  };

  // 4) Şablon güncelleme fonksiyonu
  const handleEdit = sig => { setFormError(null); setEditSig(sig); };
  const handleUpdate = async payload => {
    setFormError(null);
    const body = {
      name: payload.name,
      html_content: payload.html,
      language: payload.language,
      language_variants: payload.language_variants,
      assignments: payload.assignments.map(a => ({
        group_name: a.group,
        priority: a.priority
      })),
      user_assignments: payload.user_assignments,
      survey: payload.survey,
    };
    try {
      await api.put(endpoints.signatures.templates.detail(editSig.id), body);
      setEditSig(null);
      await loadSignatures();
    } catch (err) {
      setFormError(t('updateFailed', { msg: describeError(err) }));
    }
  };

  // 5) Şablonu çoğalt — mevcut create endpoint'ini "{isim} (Kopya)" ile
  // yeniden kullanır, ayrı bir backend endpoint'i gerekmiyor.
  const handleDuplicate = async (sig) => {
    setError(null);
    const body = {
      name: `${sig.name} ${t('copySuffix')}`,
      html_content: sig.html_content,
      language: sig.language || 'tr',
      // Faithful full duplicate — previously silently dropped any non-
      // default language variants, so "Çoğalt" on a multi-language
      // template quietly produced a single-language copy.
      language_variants: (sig.language_variants || []).map(v => ({
        language: v.language, html_content: v.html_content,
      })),
      organization: user.organization,
      assignments: Array.isArray(sig.assignments)
        ? sig.assignments.map(a => ({ group_name: a.group_name, priority: a.priority }))
        : [],
      // Direct user assignments are deliberately NOT copied: the newer copy
      // would silently take those people over at equal priority.
      survey: sig.survey && sig.survey.is_active ? { scale: sig.survey.scale, style: sig.survey.style, question: sig.survey.question } : null,
    };
    try {
      await api.post('/api/signatures/templates/create/', body);
      await loadSignatures();
    } catch (err) {
      setError(t('duplicateFailed', { msg: describeError(err) }));
    }
  };

  return (
    <div className="page-stack signatures-page">
      <div className="signatures-header">
        <div>
          <h1>{t('title')}</h1>
          <p className="signatures-subtitle">{t('subtitle', { n: signatures.length })}</p>
        </div>
      </div>

      <div className="signatures-toolbar">
        <div className="signatures-search">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" />
          </svg>
          <input
            type="text"
            placeholder={t('search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Button onClick={() => { setFormError(null); setShowForm(true); }}>{t('newTemplate')}</Button>
      </div>

      {error && <Alert variant="error">{t('error', { msg: error })}</Alert>}

      {(showForm || editSig) && (
        <div className="modal-overlay" {...overlayClose}>
          <div className="modal-window">
            {groupsLoading ? (
              <div>{t('groupsLoading')}</div>
            ) : (
              <SignatureForm
                onSave={editSig ? handleUpdate : handleSave}
                onCancel={() => { setShowForm(false); setEditSig(null); setFormError(null); }}
                error={formError}
                title={editSig ? t('editTitle') : t('createTitle')}
                groups={groups}
                initialName={editSig && typeof editSig.name === 'string' ? editSig.name : ''}
                initialHtml={editSig && typeof editSig.html_content === 'string' ? editSig.html_content : (editSig && editSig.html_content ? String(editSig.html_content) : '')}
                initialLanguage={editSig && editSig.language ? editSig.language : 'tr'}
                initialLanguageVariants={Array.isArray(editSig?.language_variants) ? editSig.language_variants : []}
                initialAssignments={Array.isArray(editSig?.assignments) ? editSig.assignments : []}
                initialUserAssignments={Array.isArray(editSig?.user_assignments) ? editSig.user_assignments : []}
                users={directoryUsers}
                initialSurvey={editSig && editSig.survey ? editSig.survey : null}
                brandColors={brandColors}
                brandAssets={brandAssets}
                key={editSig ? `edit-${editSig.id}` : 'new'}
              />
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : (
        <div className="signatures-grid">
          {filteredSignatures.map(sig => (
            <div className={`signature-card ${sig.is_active === false ? 'signature-card-paused' : ''}`.trim()} key={sig.id}>
              <div
                className="signature-card-preview"
                dangerouslySetInnerHTML={{ __html: sig.html_content }}
              />
              <div className="signature-card-body">
                <div className="signature-card-title-row">
                  <div className="signature-card-name" title={sig.name}>{sig.name}</div>
                  <div className="signature-card-badges">
                    {(() => {
                      const langs = [sig.language, ...(sig.language_variants || []).map(v => v.language)];
                      const label = LANGUAGE_ORDER.filter(code => langs.includes(code)).map(code => code.toUpperCase()).join('/');
                      // A single 'tr' (the overwhelmingly common case) shows no
                      // badge at all, unchanged from before this feature — only
                      // a single 'en' or an actual multi-language template
                      // (now possible within ONE template) gets one.
                      return (langs.length > 1 || sig.language === 'en') && (
                        <span className="signature-status-badge inactive" title={t('langTitle', { label })}>{label}</span>
                      );
                    })()}
                    <span className={`signature-status-badge ${sig.is_active === false ? 'inactive' : 'active'}`}>
                      {sig.is_active === false ? t('inactive') : t('active')}
                    </span>
                  </div>
                </div>
                {((sig.assignments || []).length > 0 || (sig.user_assignments || []).length > 0) && (
                  <div className="signature-card-pills">
                    {(sig.assignments || []).map(a => (
                      <span className="signature-card-pill" key={a.group_name}>{a.group_name} · {a.priority}</span>
                    ))}
                    {(sig.user_assignments || []).length > 0 && (
                      <span
                        className="signature-card-pill"
                        title={sig.user_assignments.map(a => `${a.email} · ${a.priority}`).join('\n')}
                      >
                        {t('people', { n: sig.user_assignments.length })}
                      </span>
                    )}
                  </div>
                )}
                <div className="signature-card-actions">
                  <Button variant="secondary" className="signature-card-edit-btn" onClick={() => handleEdit(sig)}>{t('edit')}</Button>
                  <Button variant="secondary" onClick={() => handleDuplicate(sig)}>{t('duplicate')}</Button>
                  <Toggle
                    stretch
                    checked={sig.is_active !== false}
                    onChange={next => handleToggleActive(sig, next)}
                    aria-label={sig.is_active === false ? t('makeActive') : t('makeInactive')}
                    title={sig.is_active === false ? t('inactiveTitle') : t('activeTitle')}
                  />
                  <Button variant="danger" className="signature-card-delete-btn" onClick={() => handleDelete(sig.id)} aria-label={t('deleteAria')} title={t('delete')}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          ))}
          <button type="button" className="signature-card-new" onClick={() => { setFormError(null); setShowForm(true); }}>
            <span className="signature-card-new-icon">+</span>
            <span className="signature-card-new-title">{t('createTitle')}</span>
            <span className="signature-card-new-hint">{t('newHint')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
